import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { IntroNote } from "@/components/IntroNote";
import { Navbar } from "@/components/Navbar";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Save, Layers, EyeOff } from "lucide-react";

const TYPE_LABEL = { risk: "Risks", supplier: "Suppliers", kri: "KRIs", remediation: "Remediation plans" };
const CLEARANCE = [
  { v: 1, l: "L1 — General" }, { v: 2, l: "L2 — Internal" }, { v: 3, l: "L3 — Restricted" },
  { v: 4, l: "L4 — Confidential" }, { v: 5, l: "L5 — Executive" },
];

export default function AccessPolicy() {
  const [data, setData] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/access-policy")
      .then(({ data }) => { setData(data); setPolicy(data.policy); })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load policy."));
  }, []);

  const setField = (k, v) => setPolicy((p) => ({ ...p, [k]: v }));
  const toggleMasked = (rtype, key) => setPolicy((p) => {
    const cur = new Set(p.masked_fields?.[rtype] || []);
    cur.has(key) ? cur.delete(key) : cur.add(key);
    return { ...p, masked_fields: { ...p.masked_fields, [rtype]: [...cur] } };
  });

  const save = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.put("/access-policy", policy);
      setPolicy(res.policy);
      toast.success("Access policy saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save policy.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-3xl px-6 py-8" data-testid="access-policy-page">
        <Seo title="Access policy" description="Configure granular record-level visibility and field-level redaction per tenant." path="/access-policy" />
        <p className="ek-eyebrow">Owner / Org admin · Access governance</p>
        <h1 className="ek-heading mt-1 text-3xl">Access policy</h1>

        <IntroNote>
          Control what each user can see at a granular level. <b>Business-unit scoping</b> restricts users
          (who have units assigned under Users &amp; access) to their own unit's records. <b>Field redaction</b>
          masks sensitive fields for anyone below the clearance threshold you set.
        </IntroNote>

        {!policy ? (
          <p className="text-sm text-ek-ink/65">Loading…</p>
        ) : (
          <div className="space-y-6">
            {/* Business-unit scoping */}
            <div className="rounded-xl border border-black/5 bg-white p-6" data-testid="bu-scoping-card">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="flex items-center gap-2 font-display text-base font-semibold text-ek-ink"><Layers size={17} className="text-ek-purple" /> Business-unit scoping</p>
                  <p className="mt-0.5 text-sm text-ek-ink/65">Users with assigned units see only untagged records plus their own unit's. Owner &amp; unrestricted users are unaffected.</p>
                </div>
                <Switch checked={policy.bu_scoping_enabled} onCheckedChange={(v) => setField("bu_scoping_enabled", v)} data-testid="bu-scoping-toggle" />
              </div>
              {data?.business_units?.length > 0 && (
                <p className="mt-3 text-xs text-ek-ink/65">Units in use: {data.business_units.map((b) => (
                  <span key={b} className="ml-1 rounded-full bg-ek-canvas px-2 py-0.5 font-mono">{b}</span>
                ))}</p>
              )}
            </div>

            {/* Field redaction */}
            <div className="rounded-xl border border-black/5 bg-white p-6" data-testid="redaction-card">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="flex items-center gap-2 font-display text-base font-semibold text-ek-ink"><EyeOff size={17} className="text-ek-purple" /> Field-level redaction</p>
                  <p className="mt-0.5 text-sm text-ek-ink/65">Mask sensitive fields for viewers below the clearance threshold.</p>
                </div>
                <Switch checked={policy.redaction_enabled} onCheckedChange={(v) => setField("redaction_enabled", v)} data-testid="redaction-toggle" />
              </div>

              {policy.redaction_enabled && (
                <div className="mt-5 space-y-5">
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-ek-ink">Mask for clearance below</label>
                    <Select value={String(policy.redaction_min_clearance)} onValueChange={(v) => setField("redaction_min_clearance", Number(v))}>
                      <SelectTrigger className="w-48" data-testid="redaction-clearance-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CLEARANCE.map((c) => <SelectItem key={c.v} value={String(c.v)}>{c.l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {data.record_types.map((rtype) => (
                    <div key={rtype} data-testid={`masked-fields-${rtype}`}>
                      <p className="mb-2 text-sm font-semibold text-ek-ink">{TYPE_LABEL[rtype] || rtype}</p>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {data.catalogue[rtype].map((f) => {
                          const checked = (policy.masked_fields?.[rtype] || []).includes(f.key);
                          return (
                            <label key={f.key} className="flex cursor-pointer items-center gap-2 rounded-lg border border-black/5 bg-ek-canvas px-3 py-2 text-sm text-ek-ink/70 hover:bg-ek-purple/5">
                              <input type="checkbox" checked={checked} onChange={() => toggleMasked(rtype, f.key)} data-testid={`mask-${rtype}-${f.key}`} className="accent-ek-purple" />
                              {f.label}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button type="button" onClick={save} disabled={saving} data-testid="save-policy-button" className="ek-btn disabled:opacity-60">
              <Save size={16} /> {saving ? "Saving…" : "Save policy"}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
